import type { ReactNode } from "react";

import type { DesignSystemElementProps, DesignSystemRef } from "@/design-system/contracts/dom";
import { Inline } from "@/design-system/Inline";

export type ActivityEntryProps = {
  avatar: ReactNode;
  header: ReactNode;
  timestamp: ReactNode;
  children: ReactNode;
} & DesignSystemElementProps<"article"> &
  DesignSystemRef<HTMLElement>;

/** The shared avatar, metadata, and content shell for one activity entry. */
export function ActivityEntry({
  avatar,
  children,
  header,
  ref,
  timestamp,
  ...nativeProps
}: ActivityEntryProps): ReactNode {
  return (
    <article {...nativeProps} ref={ref} className="flex gap-space-sm overflow-hidden p-space-sm">
      {avatar}
      <section className="grid flex-1 grid-cols-1 pt-space-xs">
        <Inline gap="xs" align="center">
          {header}
          <span className="ml-space-xs text-caption text-text-secondary">{timestamp}</span>
        </Inline>
        {children}
      </section>
    </article>
  );
}

export type ActivityMessageProps = {
  children: ReactNode;
} & DesignSystemElementProps<"div"> &
  DesignSystemRef<HTMLDivElement>;

/** The standard bordered message surface inside an ActivityEntry. */
export function ActivityMessage({ children, ref, ...nativeProps }: ActivityMessageProps): ReactNode {
  return (
    <div
      {...nativeProps}
      ref={ref}
      className="my-space-xs rounded-md border border-border-default bg-background-default p-space-sm whitespace-pre-line italic"
    >
      {children}
    </div>
  );
}
